import React from "react";
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  HStack,
  Badge,
  useColorModeValue,
  Heading,
  Container,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Menu,
  MessageSquare,
  ChevronRight,
  LayoutDashboard,
  Mail,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const CrmSuperAdminDashboard = () => {
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const dashboardCards = [
    {
      title: "Legal Content",
      description: "Manage Terms, Privacy, Security & Refund policies",
      icon: FileText,
      route: "/legal-content-list",
      color: "blue.500",
      gradient: "linear(to-br, blue.400, blue.600)",
      stats: "4 Sections",
      bgPattern:
        "radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)",
    },
    {
      title: "Navbar Content",
      description: "Edit navigation bar items and menu structure",
      icon: Menu,
      route: "/navbar-content-list",
      color: "purple.500",
      gradient: "linear(to-br, purple.400, purple.600)",
      stats: "Dynamic Menu",
      bgPattern:
        "radial-gradient(circle at 20% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)",
    },
    {
      title: "Demo Enquiries",
      description: "View and manage demo enquiries from users",
      icon: MessageSquare,
      route: "/demo-enquiry-list",
      color: "green.500",
      gradient: "linear(to-br, green.400, green.600)",
      stats: "All Enquiries",
      bgPattern:
        "radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.1) 0%, transparent 50%)",
    },
    {
      title: "Contact Enquiries",
      description: "Manage contact form submissions from website visitors",
      icon: Mail,
      route: "/contact-enquiry-list",
      color: "orange.500",
      gradient: "linear(to-br, orange.400, orange.600)",
      stats: "All Messages",
      bgPattern:
        "radial-gradient(circle at 70% 30%, rgba(249, 115, 22, 0.1) 0%, transparent 50%)",
    },
    {
      title: "Footer Content",
      description: "Manage footer links, company info, and quick navigation",
      icon: LayoutDashboard,
      route: "/footer-content-list",
      color: "pink.500",
      gradient: "linear(to-br, pink.400, pink.600)",
      stats: "Live Footer",
      bgPattern:
        "radial-gradient(circle at 60% 40%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)",
    },
  ];

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Modern Header Section */}
      <Box
        position="relative"
        bgGradient="linear(to-br, blue.600, purple.600, pink.500)"
        overflow="hidden"
        mb={4}
      >
        {/* Animated Background Elements */}
        <Box
          position="absolute"
          top="-50%"
          right="-10%"
          w="500px"
          h="500px"
          borderRadius="full"
          bg="whiteAlpha.100"
          filter="blur(80px)"
          animation="float 20s infinite ease-in-out"
        />
        <Box
          position="absolute"
          bottom="-30%"
          left="-5%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="whiteAlpha.100"
          filter="blur(60px)"
          animation="float 25s infinite ease-in-out reverse"
        />

        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              33% { transform: translate(30px, -30px) rotate(5deg); }
              66% { transform: translate(-20px, 20px) rotate(-5deg); }
            }
            
            @keyframes pulse-glow {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
            
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>

        <Container maxW="8xl" position="relative" zIndex={1}>
          <VStack
            spacing={6}
            align="start"
            py={{ base: 6, md: 8 }}
            px={{ base: 4, md: 8 }}
          >
            {/* Badge */}
            <HStack
              bg="whiteAlpha.200"
              backdropFilter="blur(10px)"
              px={4}
              py={2}
              borderRadius="full"
              border="1px"
              borderColor="whiteAlpha.300"
              animation="slideUp 0.6s ease-out"
            >
              <Icon as={Sparkles} boxSize={4} color="yellow.300" />
              <Text fontSize="sm" color="white" fontWeight="medium">
                CRM Management Portal
              </Text>
            </HStack>

            {/* Main Heading */}
            <VStack align="start" spacing={4}>
              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="black"
                color="white"
                lineHeight="1.2"
                textShadow="0 4px 20px rgba(0,0,0,0.2)"
                animation="slideUp 0.8s ease-out"
              >
                CRM Super Admin Dashboard
              </Heading>

              <Text
                fontSize={{ base: "xs", md: "lg" }}
                color="whiteAlpha.900"
                maxW="2xl"
                lineHeight="1.7"
                animation="slideUp 1s ease-out"
              >
                Centralized control panel to manage all website content,
                navigation, and customer enquiries. Access and modify Matrix CRM
                platform with ease.
              </Text>
            </VStack>
          </VStack>
        </Container>

        {/* Bottom Wave */}
        <Box
          position="absolute"
          bottom="-20px"
          left="0"
          right="0"
          height="60px"
        >
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{ width: "100%", height: "100%" }}
          >
            <path
              d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
              fill="#F7FAFC"
            />
          </svg>
        </Box>
      </Box>

      {/* Dashboard Cards Section */}
      <Container maxW="8xl" px={{ base: 4, md: 8 }} pb={12}>
        {/* Section Header */}
        <VStack align="start" spacing={2} mb={8}>
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            color="gray.800"
          >
            Quick Access
          </Heading>
          <Text fontSize="md" color="gray.600">
            Select a module to manage Matrix CRM Website content
          </Text>
        </VStack>

        {/* Navigation Cards Grid */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 4 }}
          spacing={{ base: 4, md: 6 }}
        >
          {dashboardCards.map((card, index) => (
            <Box
              key={index}
              position="relative"
              bg={cardBg}
              borderRadius="2xl"
              border="1px"
              borderColor={borderColor}
              overflow="hidden"
              cursor="pointer"
              onClick={() => handleCardClick(card.route)}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                transform: "translateY(-8px)",
                boxShadow: "2xl",
                borderColor: card.color,
              }}
              role="group"
              animation={`slideUp ${0.4 + index * 0.1}s ease-out`}
            >
              {/* Background Pattern */}
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                background={card.bgPattern}
                opacity="1"
                transition="opacity 0.3s"
                _groupHover={{ opacity: "1" }}
              />

              {/* Card Content */}
              <Box position="relative" p={6}>
                {/* Icon and Badge Row */}
                <HStack justify="space-between" mb={4}>
                  <Box
                    p={3}
                    bgGradient={card.gradient}
                    borderRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s"
                    boxShadow="md"
                    _groupHover={{
                      transform: "scale(1.1) rotate(5deg)",
                      boxShadow: "lg",
                    }}
                  >
                    <Icon as={card.icon} boxSize={6} color="white" />
                  </Box>
                  <Badge
                    colorScheme={card.color.split(".")[0]}
                    fontSize="xs"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    {card.stats}
                  </Badge>
                </HStack>

                {/* Title */}
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="gray.800"
                  mb={2}
                  transition="color 0.3s"
                  _groupHover={{ color: card.color }}
                >
                  {card.title}
                </Text>

                {/* Description */}
                <Text fontSize="sm" color="gray.600" mb={4} lineHeight="1.6">
                  {card.description}
                </Text>

                {/* Action Link */}
                <HStack
                  spacing={2}
                  color={card.color}
                  fontWeight="medium"
                  fontSize="sm"
                  transition="all 0.3s"
                  _groupHover={{
                    transform: "translateX(4px)",
                  }}
                >
                  <Text>Manage</Text>
                  <Icon as={ChevronRight} boxSize={4} />
                </HStack>
              </Box>

              {/* Bottom Accent Line */}
              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                h="4px"
                bgGradient={card.gradient}
                transform="scaleX(0)"
                transformOrigin="left"
                transition="transform 0.3s"
                _groupHover={{
                  transform: "scaleX(1)",
                }}
              />
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CrmSuperAdminDashboard;
